import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

/**
 * useUnreadModules
 * - Pobiera najnowsze createdAt dla modułów "Sprawy restauracji" i "Rozwój zespołu".
 * - Porównuje z timestampem ostatniego wejścia użytkownika (per moduł + lokalizacja, w localStorage).
 * - Zwraca obiekt { restaurant_matters: bool, development: bool, markSeen(moduleId) }.
 *
 * Polling co 60s. Pierwsza ankietyzacja przy mount.
 */
export function useUnreadModules({ userId, location }) {
  const [flags, setFlags] = useState({ restaurant_matters: false, development: false });
  const latestRef = useRef({ restaurant_matters: null, development: null });

  const storageKey = useCallback(
    (moduleId) => `unread:${userId || 'anon'}:${location || 'na'}:${moduleId}`,
    [userId, location]
  );

  const computeFlag = useCallback(
    (moduleId, latestIso) => {
      if (!latestIso) return false;
      const seen = localStorage.getItem(storageKey(moduleId));
      if (!seen) return true; // nigdy nie wszedł — pokaż dot jeśli cokolwiek jest
      return new Date(latestIso).getTime() > new Date(seen).getTime();
    },
    [storageKey]
  );

  const fetchStats = useCallback(async () => {
    if (!location) return;
    try {
      const [mattersRes, devRes] = await Promise.allSettled([
        axios.get(`${API}/restaurant-matters/stats`, { params: { location } }),
        axios.get(`${API}/development/stats`, { params: { location } }),
      ]);
      const mattersLatest =
        mattersRes.status === 'fulfilled' ? mattersRes.value.data?.latestCreatedAt || null : null;
      const devLatest =
        devRes.status === 'fulfilled' ? devRes.value.data?.latestCreatedAt || null : null;

      latestRef.current = {
        restaurant_matters: mattersLatest,
        development: devLatest,
      };
      setFlags({
        restaurant_matters: computeFlag('restaurant_matters', mattersLatest),
        development: computeFlag('development', devLatest),
      });
    } catch (e) {
      /* cicho - to tylko wskaźnik */
    }
  }, [location, computeFlag]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60_000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const markSeen = useCallback(
    (moduleId) => {
      localStorage.setItem(storageKey(moduleId), new Date().toISOString());
      setFlags((prev) => ({ ...prev, [moduleId]: false }));
    },
    [storageKey]
  );

  return { flags, markSeen, refresh: fetchStats };
}
